import React from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { graphql, useFragment } from 'react-relay';
import {
	type Project,
	ProjectStyle,
	TEAM_MANAGED_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import {
	FeedbackButton,
	CAPACITY_FEEDBACK_ROUTES,
} from '@atlassian/jira-capacity/src/ui/feedback-button/FeedbackButton.tsx';
import { ShareButton } from '../share-button/index.tsx';
import {
	HeaderFullscreenButton,
	SUPPORTED_ROUTES_EXTENDED,
	SUPPORTED_ROUTES,
} from '../fullscreen-button/index.tsx';
import type { ExtraComponentsFar$key } from './__generated__/ExtraComponentsFar.graphql';
import { AutomationButton } from './automation-button/AutomationButton.tsx';

type ExtraComponentProps = {
	projectFragment: ExtraComponentsFar$key | null;
};

// Components that go on the far right side of the header
export const ExtraComponentsFar = ({ projectFragment }: ExtraComponentProps) => {
	const [isFullscreen] = useIsFullscreen();
	const { name: currentRouteName } = useCurrentRoute();
	const isAnonymous = useIsAnonymous();
	const shouldShowCapacityFeedback =
		fg('add_capacity_tab_to_the_board_horizontal_nav') &&
		CAPACITY_FEEDBACK_ROUTES.includes(currentRouteName);

	const project = useFragment(
		graphql`
			fragment ExtraComponentsFar on JiraProject {
				key
				name
				projectId
				projectStyle
				projectType
				avatar {
					large
				}
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
			}
		`,
		projectFragment,
	);

	const hasFullscreenButton = (
		fg('jira-full-screen') ? SUPPORTED_ROUTES_EXTENDED : SUPPORTED_ROUTES
	).includes(currentRouteName);

	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;
	const shouldShowAutomation = !isAnonymous && project && canEditProjectConfig;
	const isSimplified = project?.projectStyle === TEAM_MANAGED_PROJECT;

	let projectShareContext: Project | null = null;
	if (project) {
		projectShareContext = {
			avatarUrl: project?.avatar?.large ?? '',
			id: Number(project.projectId),
			isSimplified,
			key: project.key,
			name: project.name,
			style:
				project.projectStyle === TEAM_MANAGED_PROJECT
					? ProjectStyle.NEXT_GEN
					: ProjectStyle.CLASSIC,
			type: project?.projectType ?? '',
		};
	}

	if (fg('jira-full-screen')) {
		if (isFullscreen) {
			if (hasFullscreenButton) {
				return <HeaderFullscreenButton key="fullscreen" />;
			}
			return null;
		}

		return (
			<>
				{projectShareContext && !isAnonymous && (
					<SpotlightManager key="share-spotlight">
						<SpotlightTarget name="ccEcoBoardBacklogShare">
							<ShareButton data={projectShareContext} key="Share" />
						</SpotlightTarget>
						<EngagementSpotlight name="ccEcoBoardBacklogShare" />
					</SpotlightManager>
				)}
				{shouldShowAutomation && <AutomationButton key="automation" />}
				{shouldShowCapacityFeedback && (
					<FeedbackButton key="feedback" routeName={currentRouteName} />
				)}
				{hasFullscreenButton && <HeaderFullscreenButton key="fullscreen" />}
			</>
		);
	}

	const componentsToShow: React.ReactNode[] = [];

	if (isFullscreen) {
		if (hasFullscreenButton) {
			componentsToShow.push(<HeaderFullscreenButton key="fullscreen" />);
			return componentsToShow;
		}
		return null;
	}

	if (hasFullscreenButton) {
		componentsToShow.push(<HeaderFullscreenButton key="fullscreen" />);
	}

	if (projectShareContext && !isAnonymous) {
		componentsToShow.push(
			<SpotlightManager key="share-spotlight">
				<SpotlightTarget name="ccEcoBoardBacklogShare">
					<ShareButton data={projectShareContext} key="Share" />
				</SpotlightTarget>
				<EngagementSpotlight name="ccEcoBoardBacklogShare" />
			</SpotlightManager>,
		);
	}

	if (shouldShowAutomation) {
		componentsToShow.push(<AutomationButton key="automation" />);
	}

	if (shouldShowCapacityFeedback) {
		componentsToShow.push(<FeedbackButton key="feedback" routeName={currentRouteName} />);
	}

	return componentsToShow;
};
